import type { PausedAt, Workflow, WorkflowNode, WorkflowRun } from "../types"

// --- Node handler interface ---
//
// Each node type registers a handler that executes the node given the live
// run context. Handlers are awaited inside the dispatcher's walk loop; they
// are NOT on the webhook-response critical path, so taking tens of seconds
// (e.g. for an agent call) is fine.
//
// Handlers return one of three shapes:
//   - ok: { output }             — node produced data, carry on
//   - ok with port: { output, port } — branch nodes: which outgoing port fired
//   - paused: { paused: true, pausedAt } — checkpoint nodes (Phase 2)
//   - failed: { error }          — log + fail the run

export interface AgentExecuteRequest {
  agentId: string
  message: string
  workflowRunId?: string
  timeoutMinutes?: number
  /** Step autonomy (report | propose | act). Unset = act. */
  autonomy?: "report" | "propose" | "act"
}

export interface AgentExecuteResponse {
  content: string
  error?: string
  /** Typed discriminator for `error`. Common values: `out_of_credits`,
   *  `rate_limit`, `auth`, `timeout`, `overage_disabled`, `unknown`. Lets a
   *  downstream `branch` node route on the failure cause without parsing
   *  the friendly message. The full taxonomy lives in
   *  src/agents/error-map.ts → FriendlyError.kind. */
  errorKind?: string
  taskId?: string
  durationMs?: number
  /** Tool calls blocked by the step's autonomy level. */
  autonomyBlocks?: Array<{ tool: string; target: string | null; ruleId: string; reason: string }>
}

export interface NodeContext {
  workflow: Workflow
  run: WorkflowRun
  node: WorkflowNode
  /** Live channel adapter instances keyed by name (gitlab/github/telegram/...).
   *  Handlers narrow to the method they need. */
  channels: Record<string, unknown>
  /** Thin agent-execute shim around AgentRegistry.execute(). */
  agents: { execute(req: AgentExecuteRequest): Promise<AgentExecuteResponse> }
  /** Outbound mesh forwarder — used by `action.send` to deliver to a channel
   *  hosted on a peer node (e.g. workflow on macbook, whatsapp on
   *  peer-server). Optional; absent in single-node setups and tests. */
  forwardChannelSend?: (payload: {
    channel: string
    chatId: string
    text: string
    accountId?: string
    parseMode?: string
    replyTo?: string
  }) => Promise<{ messageId: string | null }>
  /** Structured log sink. */
  log: (msg: string) => void
  /** The owner of this node: told things, asked things (#788). Absent
   *  in tests and setups without a daemon; owner.* steps then fail. */
  owner?: OwnerPort
  /** Stall time, nudges and approval mode for follow-up steps. */
  followUp?: FollowUpDefaults
}

/** How the engine reaches the owner. The daemon implements it with its
 *  notify path and decision cards (src/approvals). */
export interface OwnerPort {
  /** Tell the owner. Resolves once it was handed to a channel. */
  notify(text: string, run: { id: string; workflowId: string; meta?: { title?: string; startedBy?: string } }): Promise<void>
  /** Raise a decision card about this run's step. The card's answer comes
   *  back through WorkflowDispatcher.resumeFromCard. */
  ask(card: OwnerCardInput, run: { id: string; workflowId: string; meta?: { startedBy?: string } }, nodeId: string): Promise<{ cardId: string }>
}

export interface OwnerCardInput {
  title: string
  ask: string
  recommend: string
  /** Text the owner may edit before saying yes (a message to a person). */
  draft?: string
  choices?: string[]
  /** A few lines of background shown above the question. */
  context?: string
  /** ISO time, or relative like "2d". Default: the card setting. */
  expires?: string
}

export interface FollowUpDefaults {
  stallMinutes: number
  maxNudges: number
  approval: "start" | "step"
}

export const DEFAULT_FOLLOW_UP: FollowUpDefaults = { stallMinutes: 30, maxNudges: 2, approval: "step" }

export interface NodeResult {
  /** Output bundle keyed under ctx.run.context[node.id] by the dispatcher. */
  output?: Record<string, unknown>
  /** For branch nodes: which outgoing port fired (case's `to` field or
   *  `default` port). Undefined for non-branch nodes. */
  port?: string
  /** True when the node paused the run (checkpoint, userTask, subProcess,
   *  signalWait, timerWait). Dispatcher stops the walk and parks the run
   *  until a matching event arrives. */
  paused?: boolean
  pausedAt?: PausedAt
  /** Optional actor ids who should see this task right now (userTask only). */
  assignedTo?: string[]
  /** Child workflow id + run id (subProcess only). The dispatcher spawns
   *  the child before parking the parent. */
  spawnChild?: { workflowId: string; input: Record<string, unknown> }
  /** Signal emission produced by a signal.emit node, posted to the bus
   *  before walk continues. */
  emitSignal?: { name: string; scope: "workflow" | "global"; payload: Record<string, unknown> }
  /** On failure, a short message. Dispatcher logs it + marks the run failed. */
  error?: string
  /** Follow-up (#788): the step is blocked and needs the owner. The run
   *  pauses (pausedAt) and the owner is told this reason once. */
  blocked?: string
}

export type NodeHandler = (ctx: NodeContext) => Promise<NodeResult>
